import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  Home,
  Clock,
  ThumbsUp,
  ThumbsDown,
  ChevronLeft,
  Printer,
} from 'lucide-react';
import { MarkdownRenderer } from '@/components/help/MarkdownRenderer';
import { HelpTableOfContents } from '@/components/help/HelpTableOfContents';
import { HelpArticleCard } from '@/components/help/HelpArticleCard';
import { useHelpArticle, useHelpArticles, trackHelpArticleView } from '@/hooks/useHelp';
import { Button } from '@/components/ui/Button';

export function HelpArticle() {
  const { t } = useTranslation();
  const { category: categorySlug, slug: articleSlug } = useParams<{
    category: string;
    slug: string;
  }>();
  const navigate = useNavigate();

  const articleQuery = useHelpArticle(categorySlug, articleSlug);
  const allArticlesQuery = useHelpArticles();

  const article = articleQuery.data;
  const allArticles = allArticlesQuery.data ?? [];

  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [wasHelpful, setWasHelpful] = useState<boolean | null>(null);

  useEffect(() => {
    if (article) {
      trackHelpArticleView(article.id);
    }
  }, [article?.id]);

  const relatedArticles = allArticles
    .filter((a) => {
      if (!article) return false;
      if (a.id === article.id) return false;
      // Same category or overlapping keywords
      return (
        a.category === article.category ||
        a.search_keywords.some((kw) => article.search_keywords.includes(kw))
      );
    })
    .slice(0, 3);

  const categoryArticles = allArticles.filter((a) => a.category_slug === categorySlug);
  const currentIndex = categoryArticles.findIndex((a) => a.slug === articleSlug);
  const prevArticle = currentIndex > 0 ? categoryArticles[currentIndex - 1] : null;
  const nextArticle =
    currentIndex >= 0 && currentIndex < categoryArticles.length - 1
      ? categoryArticles[currentIndex + 1]
      : null;

  const handleFeedback = (helpful: boolean) => {
    setWasHelpful(helpful);
    setFeedbackGiven(true);

    // TODO: Send feedback to backend
    console.log('Feedback:', helpful);
  };

  const handlePrint = () => {
    window.print();
  };

  if (articleQuery.isLoading || allArticlesQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page-light dark:bg-page-dark">
        <div className="text-gray-500">{t('app.loading', 'Indlæser...')}</div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-page-light dark:bg-page-dark">
        <div className="mx-auto max-w-7xl px-6 py-12">
          <div className="rounded-lg border border-gray-200 bg-white p-12 text-center dark:border-gray-700 dark:bg-card-dark">
            <p className="text-gray-500 dark:text-gray-400">
              {t('help.article_not_found', 'Artikel ikke fundet')}
            </p>
            <Link
              to="/help"
              className="mt-4 inline-block text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
            >
              {t('help.back_to_center', 'Tilbage til hjælpecenter')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const readTime = Math.max(1, Math.ceil(article.content.split(/\s+/).length / 200));

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark print:bg-white">
      <main className="mx-auto max-w-7xl px-6 py-12">
        {/* Breadcrumb */}
        <nav className="mb-6 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 print:hidden">
          <Link
            to="/help"
            className="flex items-center gap-1 hover:text-indigo-600 dark:hover:text-indigo-400"
          >
            <Home className="h-4 w-4" />
            {t('help.home', 'Hjælpecenter')}
          </Link>
          <ChevronRight className="h-4 w-4" />
          <Link
            to={`/help/${categorySlug}`}
            className="hover:text-indigo-600 dark:hover:text-indigo-400"
          >
            {article.category_title}
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span className="font-medium text-gray-900 dark:text-white">{article.title}</span>
        </nav>

        <div className="grid gap-8 lg:grid-cols-[1fr_250px]">
          {/* Main content */}
          <div>
            {/* Article header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white print:text-3xl">
                {article.title}
              </h1>
              <p className="mt-3 text-lg text-gray-600 dark:text-gray-400">
                {article.excerpt}
              </p>
              <div className="mt-4 flex items-center gap-4 text-sm text-gray-500 dark:text-gray-500">
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {readTime} {t('help.min_read', 'min læsning')}
                </span>
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300 print:hidden"
                >
                  <Printer className="h-4 w-4" />
                  {t('help.print', 'Udskriv')}
                </button>
              </div>
            </div>

            {/* Article content */}
            <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-700 dark:bg-card-dark print:border-0 print:shadow-none">
              <MarkdownRenderer content={article.content} />
            </div>

            {/* Feedback */}
            <div className="mt-8 rounded-xl border border-gray-200 bg-white p-6 text-center dark:border-gray-700 dark:bg-card-dark print:hidden">
              {!feedbackGiven ? (
                <>
                  <p className="mb-4 text-sm font-medium text-gray-900 dark:text-white">
                    {t('help.was_helpful', 'Var denne artikel nyttig?')}
                  </p>
                  <div className="flex justify-center gap-3">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleFeedback(true)}
                      className="flex items-center gap-2"
                    >
                      <ThumbsUp className="h-4 w-4" />
                      {t('help.yes', 'Ja')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleFeedback(false)}
                      className="flex items-center gap-2"
                    >
                      <ThumbsDown className="h-4 w-4" />
                      {t('help.no', 'Nej')}
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {wasHelpful
                    ? t('help.thanks_helpful', 'Tak for din feedback!')
                    : t('help.thanks_not_helpful', 'Tak for din feedback. Vi arbejder på at forbedre vores guides.')}
                </p>
              )}
            </div>

            {/* Related articles */}
            {relatedArticles.length > 0 && (
              <div className="mt-12 print:hidden">
                <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                  {t('help.related_articles', 'Relaterede artikler')}
                </h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {relatedArticles.map((related) => (
                    <HelpArticleCard key={related.id} article={related} showCategory />
                  ))}
                </div>
              </div>
            )}

            {/* Previous / Next navigation */}
            {(prevArticle || nextArticle) && (
              <div className="mt-8 flex justify-between gap-4 print:hidden">
                {prevArticle ? (
                  <button
                    onClick={() => navigate(`/help/${categorySlug}/${prevArticle.slug}`)}
                    className="flex flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-white p-4 text-left transition-colors hover:border-indigo-300 hover:bg-indigo-50 dark:border-gray-700 dark:bg-card-dark dark:hover:border-indigo-600 dark:hover:bg-indigo-900/20"
                  >
                    <ChevronLeft className="h-5 w-5 shrink-0 text-gray-400" />
                    <div className="min-w-0">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {t('help.previous', 'Forrige')}
                      </div>
                      <div className="truncate text-sm font-medium text-gray-900 dark:text-white">
                        {prevArticle.title}
                      </div>
                    </div>
                  </button>
                ) : (
                  <div className="flex-1" />
                )}

                {nextArticle && (
                  <button
                    onClick={() => navigate(`/help/${categorySlug}/${nextArticle.slug}`)}
                    className="flex flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-white p-4 text-right transition-colors hover:border-indigo-300 hover:bg-indigo-50 dark:border-gray-700 dark:bg-card-dark dark:hover:border-indigo-600 dark:hover:bg-indigo-900/20"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {t('help.next', 'Næste')}
                      </div>
                      <div className="truncate text-sm font-medium text-gray-900 dark:text-white">
                        {nextArticle.title}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Sidebar: Table of contents */}
          <aside className="print:hidden">
            <HelpTableOfContents content={article.content} />
          </aside>
        </div>
      </main>
    </div>
  );
}
